import {useCallback} from 'react';
import {createNextState} from '@reduxjs/toolkit';
import {useQueryClient, useMutation} from 'react-query';

import {Survey, SurveySlug, BoxContext, BoxType, UXContext} from 'client/common/types';
import {SurveyPreset} from '../types/survey';

import {updateSurveyBulk} from 'client/common/utils/api-client';

import {getSurveyKey, useSurvey, useSurveyPurpose} from 'client/common/hooks/survey';

export function useBoxType(slug: SurveySlug): BoxType | undefined {
    const survey = useSurvey(slug);

    return survey.data?.box?.name;
}

export function useBoxContext(slug: SurveySlug): BoxContext | undefined {
    const survey = useSurvey(slug);

    return survey.data?.box?.context;
}

export function useUpdateBoxContext<T extends BoxContext>(slug: SurveySlug) {
    const key = getSurveyKey(slug);

    const queryClient = useQueryClient();

    const {mutateAsync} = useMutation(updateSurveyBulk, {
        onMutate: async ({data: {box}}) => {
            await queryClient.cancelQueries(key);

            const shapshot = queryClient.getQueryData(key);

            queryClient.setQueryData(key, (old: Survey) => ({
                ...old,
                box
            }));

            return shapshot;
        },
        onError: (_error, _, shapshot) => {
            queryClient.setQueryData(key, shapshot);
        }
    });

    return useCallback(
        async (update: ((context: T) => Partial<T>) | Partial<T>) => {
            const survey = queryClient.getQueryData<Survey>(key);

            const context = survey?.box?.context as T;

            const params = {
                slug,
                data: {
                    box: {
                        name: survey?.box?.name,
                        context: {
                            ...context,
                            ...(typeof update === 'function'
                                ? createNextState(context, update)
                                : update)
                        }
                    }
                }
            };

            await mutateAsync(params);
        },
        [key, slug, mutateAsync, queryClient]
    );
}

export function useUpdateEmail(slug: SurveySlug) {
    const updateBoxContext = useUpdateBoxContext<UXContext>(slug);

    return async (email: string) => {
        await updateBoxContext({email});
    };
}

export function useSurveyPreset(slug: SurveySlug): SurveyPreset | undefined {
    const purpose = useSurveyPurpose(slug);
    const surveyType = useBoxType(slug) ?? 'PRO';

    if (purpose?.selected) {
        return purpose.selected;
    }

    switch (surveyType) {
        case 'PRO':
            return SurveyPreset.PRO;
        case 'ux':
            return SurveyPreset.UX;
    }
}
